import React, { useRef, useState, useEffect } from 'react';
import './index.scss';
import Button from '../../Button';
import ProgressBar from '../../ProgressBar';

export default function AudioExtractor() {
  const [videoUrl, setVideoUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [videoInfo, setVideoInfo] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('webm');
  const [availableFormats, setAvailableFormats] = useState(['webm']);
  
  const videoRef = useRef();
  const inputRef = useRef();
  const audioRef = useRef();

  // 检测浏览器支持的音频格式
  const detectSupportedFormats = () => {
    const formats = [];
    
    // 浏览器原生支持的格式
    const nativeFormats = [
      { type: 'audio/webm;codecs=opus', name: 'webm', label: 'WebM (Opus)', native: true },
      { type: 'audio/webm', name: 'webm', label: 'WebM', native: true },
      { type: 'audio/mp4', name: 'mp4', label: 'MP4', native: true },
      { type: 'audio/wav', name: 'wav', label: 'WAV', native: true },
      { type: 'audio/ogg;codecs=opus', name: 'ogg', label: 'OGG (Opus)', native: true },
      { type: 'audio/ogg', name: 'ogg', label: 'OGG', native: true }
    ];

    // 检测浏览器支持哪些格式
    nativeFormats.forEach(format => {
      if (MediaRecorder.isTypeSupported(format.type)) {
        formats.push(format);
      }
    });

    // 如果没有支持的格式，至少提供WebM作为默认选项
    if (formats.length === 0) {
      formats.push({ type: 'audio/webm', name: 'webm', label: 'WebM', native: true });
    }

    return formats;
  };

  // 初始化支持的格式
  useEffect(() => {
    const formats = detectSupportedFormats();
    setAvailableFormats(formats);
    if (formats.length > 0) {
      setSelectedFormat(formats[0].name);
    }
  }, []);

  // 处理文件上传
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setError('请选择视频文件');
      return;
    }

    setError('');
    setFileName(file.name);
    setVideoUrl(URL.createObjectURL(file));
    setAudioBlob(null);
    setAudioUrl('');
    setVideoInfo(null);
  };

  // 获取视频信息
  const getVideoInfo = (video) => {
    return new Promise((resolve) => {
      video.addEventListener('loadedmetadata', () => {
        resolve({
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
          size: video.duration * 1000 // 估算大小
        });
      });
    });
  };

  // 使用MediaRecorder提取音频
  const extractWithMediaRecorder = async (video) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // 创建音频上下文
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaElementSource(video);
      const destination = audioContext.createMediaStreamDestination();

      // 连接音频节点
      source.connect(destination);
      source.connect(audioContext.destination);

      // 获取支持的格式
      const selectedFormatInfo = availableFormats.find(f => f.name === selectedFormat);
      const mimeType = selectedFormatInfo ? selectedFormatInfo.type : 'audio/webm';

      // 创建MediaRecorder
      const mediaRecorder = new MediaRecorder(destination.stream, {
        mimeType: mimeType
      });

      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: mimeType });
        resolve(audioBlob);
      };

      mediaRecorder.onerror = (event) => {
        reject(new Error('录音失败: ' + event.error));
      };

      // 开始录音
      mediaRecorder.start();
      
      // 播放视频
      video.play();

      // 监听视频结束
      video.addEventListener('ended', () => {
        mediaRecorder.stop();
        audioContext.close();
      });

      // 监听视频错误
      video.addEventListener('error', () => {
        mediaRecorder.stop();
        audioContext.close();
        reject(new Error('视频播放失败'));
      });
    });
  };

  // 提取音频
  const extractAudio = async () => {
    if (!videoUrl) {
      setError('请先上传视频文件');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setError('');

    try {
      const video = videoRef.current;
      if (!video) {
        throw new Error('视频元素未找到');
      }

      // 获取视频信息
      const info = await getVideoInfo(video);
      setVideoInfo(info);

      // 提取音频
      setProgress(50);
      const audioBlob = await extractWithMediaRecorder(video);
      
      setProgress(100);
      setAudioBlob(audioBlob);
      setAudioUrl(URL.createObjectURL(audioBlob));
      
    } catch (err) {
      setError('音频提取失败: ' + err.message);
      console.error('音频提取错误:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // 下载音频
  const downloadAudio = () => {
    if (!audioBlob) return;

    const selectedFormatInfo = availableFormats.find(f => f.name === selectedFormat);
    const extension = selectedFormatInfo ? selectedFormatInfo.name : 'webm';
    const downloadUrl = URL.createObjectURL(audioBlob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${fileName.replace(/\.[^/.]+$/, '')}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  // 重置
  const reset = () => {
    setVideoUrl('');
    setFileName('');
    setError('');
    setProgress(0);
    setAudioBlob(null);
    setAudioUrl('');
    setVideoInfo(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className="audio-extractor">
      <div className="audio-extractor-header">
        <h2>音频提取工具</h2>
        <p>从视频文件中提取音频，支持多种格式</p>
      </div>

      <div className="audio-extractor-content">
        {/* 文件上传 */}
        <div className="upload-section">
          <div className="upload-area">
            <input
              ref={inputRef}
              type="file"
              accept="video/*"
              onChange={handleFileUpload}
              className="file-input"
            />
            <div className="upload-placeholder">
              <div className="upload-icon">📹</div>
              <p>点击选择视频文件或拖拽到此处</p>
              <p className="upload-hint">支持 MP4, WebM, MOV, AVI 等格式</p>
            </div>
          </div>
        </div>

        {/* 视频预览 */}
        {videoUrl && (
          <div className="video-preview">
            <h3>视频预览</h3>
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              className="video-player"
            />
            <div className="video-info">
              <p><strong>文件名:</strong> {fileName}</p>
              {videoInfo && (
                <>
                  <p><strong>时长:</strong> {videoInfo.duration.toFixed(2)} 秒</p>
                  <p><strong>分辨率:</strong> {videoInfo.width} × {videoInfo.height}</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* 格式选择 */}
        <div className="format-selection">
          <h3>输出格式</h3>
          <select
            value={selectedFormat}
            onChange={(e) => setSelectedFormat(e.target.value)}
            className="format-select"
          >
            {availableFormats.map((format) => (
              <option key={format.name} value={format.name}>
                {format.label} {format.native ? '(原生)' : ''}
              </option>
            ))}
          </select>
          <p className="format-description">
            选择音频输出格式，所有格式均为浏览器原生支持
          </p>
        </div>

        {/* 操作按钮 */}
        <div className="action-buttons">
          <Button
            onClick={extractAudio}
            disabled={!videoUrl || isProcessing}
            className="extract-button"
          >
            {isProcessing ? '提取中...' : '提取音频'}
          </Button>
          
          <Button
            onClick={downloadAudio}
            disabled={!audioBlob}
            className="download-button"
          >
            下载音频
          </Button>
          
          <Button
            onClick={reset}
            className="reset-button"
          >
            重置
          </Button>
        </div>

        {/* 进度条 */}
        {isProcessing && (
          <div className="progress-section">
            <ProgressBar progress={progress} />
            <p className="progress-text">正在提取音频...</p>
          </div>
        )}

        {/* 错误信息 */}
        {error && (
          <div className="error-message">
            <p>❌ {error}</p>
          </div>
        )}

        {/* 音频预览 */}
        {audioUrl && (
          <div className="audio-preview">
            <h3>音频预览</h3>
            <audio
              ref={audioRef}
              src={audioUrl}
              controls
              className="audio-player"
            />
            <div className="audio-info">
              <p><strong>格式:</strong> {availableFormats.find(f => f.name === selectedFormat)?.label}</p>
              <p><strong>大小:</strong> {(audioBlob.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          </div>
        )}

        {/* 使用说明 */}
        <div className="usage-tips">
          <h3>使用说明</h3>
          <ul>
            <li>支持所有主流视频格式：MP4、WebM、MOV、AVI等</li>
            <li>输出格式为浏览器原生支持，无需额外下载</li>
            <li>WebM格式兼容性最好，推荐使用</li>
            <li>提取过程在浏览器中完成，保护隐私</li>
            <li>支持音频预览和直接下载</li>
          </ul>
        </div>
      </div>
    </div>
  );
}